/**
 * Cart Drawer — Slide-out cart panel
 * Requires window.CartDrawerConfig (set by snippets/cart-drawer.liquid)
 * Uses Shopify AJAX Cart API: /cart.js, /cart/change.js, /cart/update.js
 */
(function () {
  'use strict';

  var CFG = window.CartDrawerConfig || {};

  /* =========================================================
     Helpers
     ========================================================= */

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, CFG.moneyFormat);
    }
    return (cents / 100).toFixed(2).replace('.', ',') + ' \u20ac';
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function resizeImage(url, size) {
    if (!url) return '';
    return url.replace(/(\.[a-z]+)(\?.*)?$/i, '_' + size + '$1$2');
  }

  function debounce(fn, ms) {
    var timer;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  /* =========================================================
     CartDrawer class
     ========================================================= */

  function CartDrawer() {
    // DOM refs
    this.backdrop = document.querySelector('[data-cart-drawer-backdrop]');
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.backdrop || !this.drawer) return;

    this.bodyEl = this.drawer.querySelector('[data-cart-drawer-body]');
    this.loadingEl = this.drawer.querySelector('[data-cart-drawer-loading]');
    this.itemsList = this.drawer.querySelector('[data-cart-drawer-items]');
    this.emptyEl = this.drawer.querySelector('[data-cart-drawer-empty]');
    this.footerEl = this.drawer.querySelector('[data-cart-drawer-footer]');
    this.countEl = this.drawer.querySelector('[data-cart-drawer-count]');
    this.subtotalEl = this.drawer.querySelector('[data-cart-drawer-subtotal]');
    this.shippingBar = this.drawer.querySelector('[data-cart-drawer-shipping]');
    this.shippingMsg = this.drawer.querySelector('[data-cart-drawer-shipping-msg]');
    this.shippingProgress = this.drawer.querySelector('[data-cart-drawer-shipping-progress]');
    this.shippingMarker = this.drawer.querySelector('[data-cart-drawer-shipping-marker]');
    this.shippingTickReduced = this.drawer.querySelector('[data-cart-drawer-shipping-tick-reduced]');
    this.discountSection = this.drawer.querySelector('[data-cart-drawer-discount]');
    this.discountInput = this.drawer.querySelector('[data-cart-drawer-discount-input]');
    this.discountApplyBtn = this.drawer.querySelector('[data-cart-drawer-discount-apply]');
    this.discountError = this.drawer.querySelector('[data-cart-drawer-discount-error]');
    this.discountApplied = this.drawer.querySelector('[data-cart-drawer-discount-applied]');
    this.cartDiscountsEl = this.drawer.querySelector('[data-cart-drawer-cart-discounts]');
    this.checkoutBtn = this.drawer.querySelector('[data-cart-drawer-checkout]');
    this.itemTemplate = document.querySelector('[data-cart-drawer-item-template]');
    this.upsellSection = this.drawer.querySelector('[data-cart-drawer-upsells]');
    this.upsellList = this.drawer.querySelector('[data-cart-drawer-upsells-list]');
    this.upsellTemplate = document.querySelector('[data-cart-drawer-upsell-template]');
    this.bestsellersEl = this.drawer.querySelector('[data-cart-drawer-bestsellers]');
    this.bestsellersList = this.drawer.querySelector('[data-cart-drawer-bestsellers-list]');

    // State
    this.isOpen = false;
    this.isLoading = false;
    this.cart = null;
    this._selfUpdate = false;
    this._scrollY = 0;
    this._openTimer = null;
    this._lastUpsellProductId = null;

    this._bindEvents();
  }

  /* -------------------------------------------------------
     Public API
     ------------------------------------------------------- */

  CartDrawer.prototype.open = function () {
    if (this.isOpen) {
      this._fetchAndRender();
      return;
    }
    this.isOpen = true;

    // Save scroll position & lock body (iOS-safe)
    this._scrollY = window.pageYOffset;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + this._scrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';

    // Show loading skeleton
    this._showLoading(true);
    this.emptyEl.style.display = 'none';
    this.itemsList.innerHTML = '';
    this.footerEl.style.display = 'none';

    // Animate open
    this.backdrop.offsetHeight; // force reflow
    this.backdrop.setAttribute('data-animation-state', 'open');
    this.backdrop.setAttribute('aria-hidden', 'false');
    this.drawer.setAttribute('data-animation-state', 'open');

    // Fetch cart data
    this._fetchAndRender();

    // Focus close button for accessibility
    var closeBtn = this.drawer.querySelector('[data-cart-drawer-close]');
    setTimeout(function () { if (closeBtn) closeBtn.focus(); }, 310);
  };

  CartDrawer.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.backdrop.setAttribute('data-animation-state', 'closed');
    this.backdrop.setAttribute('aria-hidden', 'true');
    this.drawer.setAttribute('data-animation-state', 'closed');

    // Restore body scroll (iOS-safe)
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.overflow = '';
    window.scrollTo(0, this._scrollY);
  };

  /* -------------------------------------------------------
     Data fetching
     ------------------------------------------------------- */

  CartDrawer.prototype._fetchCart = function () {
    return fetch(CFG.cartUrl + '.js', {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }
    }).then(function (r) { return r.json(); });
  };

  CartDrawer.prototype._fetchAndRender = function () {
    var self = this;
    this.isLoading = true;
    this._fetchCart()
      .then(function (cart) {
        self.cart = cart;
        self.isLoading = false;
        self._renderCart(cart);
      })
      .catch(function () {
        self.isLoading = false;
        self._showLoading(false);
      });
  };

  /* -------------------------------------------------------
     Rendering
     ------------------------------------------------------- */

  CartDrawer.prototype._renderCart = function (cart) {
    this._showLoading(false);

    // Update count
    this.countEl.textContent = cart.item_count > 0 ? cart.item_count : '';

    if (cart.item_count === 0) {
      this.itemsList.innerHTML = '';
      this.emptyEl.style.display = '';
      this.footerEl.style.display = 'none';
      this.shippingBar.style.display = 'none';
      if (this.upsellSection) this.upsellSection.style.display = 'none';
      this._fetchBestsellers();
      return;
    }

    this.emptyEl.style.display = 'none';
    this.footerEl.style.display = '';

    // Free shipping bar
    this._renderShippingBar(cart);

    // Items
    this.itemsList.innerHTML = '';
    for (var i = 0; i < cart.items.length; i++) {
      var el = this._renderItem(cart.items[i]);
      if (el) this.itemsList.appendChild(el);
    }

    // Wunschfarbton-Guard: Checkout sperren, wenn eine Position keinen Farbton hat
    this._syncColorGuard();

    // Subtotal
    this.subtotalEl.textContent = formatMoney(cart.total_price);

    // Cart-level discounts
    this._renderCartDiscounts(cart);

    // Applied discount codes
    this._renderAppliedDiscounts(cart);

    // Upsells
    this._fetchUpsells(cart);
  };

  CartDrawer.prototype._renderItem = function (item) {
    if (!this.itemTemplate) return null;
    var tpl = this.itemTemplate.content.cloneNode(true);
    var li = tpl.querySelector('[data-cart-drawer-item]');
    if (!li) return null;

    li.setAttribute('data-cart-drawer-item-key', item.key);

    // Image
    var imgLink = li.querySelector('.cart-drawer__item-img');
    var img = li.querySelector('.cart-drawer__item-img img');
    if (imgLink) imgLink.href = item.url;
    if (img) {
      if (item.image) {
        img.src = resizeImage(item.image, '200x');
        img.alt = item.title || '';
      } else {
        img.style.display = 'none';
      }
    }

    // Title
    var title = li.querySelector('[data-item-title]');
    if (title) {
      title.textContent = item.product_title;
      title.href = item.url;
    }

    // Variant options
    var options = li.querySelector('[data-item-options]');
    if (options) {
      if (item.variant_title && item.variant_title !== 'Default Title') {
        options.textContent = item.variant_title;
      } else {
        options.style.display = 'none';
      }
    }

    // Line item properties
    var props = li.querySelector('[data-item-properties]');
    if (props && item.properties) {
      var propTexts = [];
      for (var key in item.properties) {
        if (item.properties.hasOwnProperty(key) && key.charAt(0) !== '_' && item.properties[key]) {
          propTexts.push(key + ': ' + item.properties[key]);
        }
      }
      if (propTexts.length > 0) {
        props.textContent = propTexts.join(' | ');
      } else {
        props.style.display = 'none';
      }
    }

    // Wunschfarbton-Guard: Bei Nachbestellungen ("Erneut kaufen") verwirft Shopify die
    // Farbauswahl-Property. Wunschfarbton-Positionen (hell/mittel/dunkel) ohne Farbton
    // markieren + zur Neuauswahl auffordern. Weiß-Stufe triggert nicht.
    var needsColor = item.variant_title &&
      item.variant_title.indexOf('Wunschfarbton') !== -1 &&
      (!item.properties || !item.properties['Farbauswahl']);
    if (needsColor) {
      li.setAttribute('data-color-missing', '');
      var info = li.querySelector('.cart-drawer__item-info');
      if (info) {
        var warn = document.createElement('div');
        warn.style.cssText = 'margin-top:6px;padding:8px 10px;border:1.5px solid #d9534f;border-radius:6px;background:#fdf3f2;';
        var warnTitle = document.createElement('div');
        warnTitle.textContent = '⚠ Farbton fehlt';
        warnTitle.style.cssText = 'font-weight:700;font-size:12px;color:#b02a25;margin-bottom:4px;';
        var warnBtn = document.createElement('a');
        warnBtn.href = item.url;
        warnBtn.textContent = 'Farbton wählen';
        warnBtn.setAttribute('data-fix-color', '');
        warnBtn.setAttribute('data-line-key', item.key);
        warnBtn.style.cssText = 'display:inline-block;padding:5px 10px;border-radius:5px;background:#007a52;color:#fff;font-size:12px;font-weight:600;text-decoration:none;';
        warn.appendChild(warnTitle);
        warn.appendChild(warnBtn);
        info.appendChild(warn);
      }
    }

    // Prices
    var compareEl = li.querySelector('[data-item-compare]');
    var priceEl = li.querySelector('[data-item-price]');
    var hasDiscount = item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0;

    if (compareEl) {
      if (item.original_price > item.final_price || hasDiscount) {
        compareEl.textContent = formatMoney(item.original_price);
      } else {
        compareEl.textContent = '';
      }
    }
    if (priceEl) {
      priceEl.textContent = formatMoney(item.final_price);
    }

    // Line total
    var lineTotal = li.querySelector('[data-item-line-total]');
    if (lineTotal) {
      lineTotal.textContent = formatMoney(item.final_line_price);
    }

    // Quantity
    var qtyInput = li.querySelector('[data-qty-input]');
    if (qtyInput) {
      qtyInput.value = item.quantity;
    }

    // Line-level discounts (XSS-safe)
    var discountsList = li.querySelector('[data-item-discounts]');
    if (discountsList && item.line_level_discount_allocations && item.line_level_discount_allocations.length > 0) {
      for (var d = 0; d < item.line_level_discount_allocations.length; d++) {
        var disc = item.line_level_discount_allocations[d];
        var discLi = document.createElement('li');
        discLi.className = 'cart-drawer__item-discount';
        discLi.innerHTML = '<svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M8.94 1.33a1.33 1.33 0 00-1.88 0L1.33 7.06a1.33 1.33 0 000 1.88l5.73 5.73c.52.52 1.36.52 1.88 0l5.73-5.73c.52-.52.52-1.36 0-1.88L8.94 1.33z"/></svg> ' +
          escapeHtml(disc.discount_application.title) + ' (-' + formatMoney(disc.amount) + ')';
        discountsList.appendChild(discLi);
      }
    }

    return li;
  };

  CartDrawer.prototype._syncColorGuard = function () {
    var missing = !!this.itemsList.querySelector('[data-color-missing]');
    if (this.checkoutBtn) {
      this.checkoutBtn.setAttribute('aria-disabled', missing ? 'true' : 'false');
      this.checkoutBtn.style.opacity = missing ? '0.5' : '';
      this.checkoutBtn.style.pointerEvents = missing ? 'none' : '';
      this.checkoutBtn.dataset.colorBlocked = missing ? 'true' : '';
    }
    var banner = this.drawer.querySelector('[data-color-guard-banner]');
    if (missing && !banner) {
      banner = document.createElement('div');
      banner.setAttribute('data-color-guard-banner', '');
      banner.textContent = '⚠ Bitte oben „Farbton wählen“ — danach ist der Checkout möglich.';
      banner.style.cssText = 'margin:8px 0;padding:8px 10px;border:1.5px solid #d9534f;border-radius:6px;background:#fdf3f2;color:#b02a25;font-size:12px;font-weight:600;';
      if (this.checkoutBtn && this.checkoutBtn.parentNode) {
        this.checkoutBtn.parentNode.insertBefore(banner, this.checkoutBtn);
      }
    } else if (!missing && banner) {
      banner.parentNode.removeChild(banner);
    }
  };

  // Staffel-Versand Fortschrittsbalken (2 Stufen): reduziert ab reducedThreshold, gratis ab freeThreshold.
  // Fortschritt läuft immer aufs Endziel (Gratis); ein Marker zeigt die reduziert-Stufe.
  CartDrawer.prototype._renderShippingBar = function (cart) {
    var T = CFG.shipping;
    if (!CFG.freeShippingEnabled || !T || !T.freeThreshold) {
      this.shippingBar.style.display = 'none';
      return;
    }

    this.shippingBar.style.display = '';
    var subtotal = cart.items_subtotal_price;
    // Komprimierte Skala: die reduziert-Stufe sitzt visuell bei markerPct (%), damit der Weg
    // von "reduziert" bis "gratis" kürzer wirkt und Gratisversand erreichbar aussieht.
    // Die €-Beträge im Text bleiben exakt — nur die Balken-Optik ist gestaucht.
    var markerPct = T.markerPct;
    var progress;
    if (subtotal >= T.freeThreshold) {
      progress = 100;
    } else if (subtotal >= T.reducedThreshold) {
      progress = markerPct + Math.round(((subtotal - T.reducedThreshold) / (T.freeThreshold - T.reducedThreshold)) * (100 - markerPct));
    } else {
      progress = Math.round((subtotal / T.reducedThreshold) * markerPct);
    }
    progress = Math.min(100, progress);

    this.shippingProgress.style.width = progress + '%';
    if (this.shippingMarker) this.shippingMarker.style.left = markerPct + '%';
    if (this.shippingTickReduced) this.shippingTickReduced.style.left = markerPct + '%';

    var reducedReached = subtotal >= T.reducedThreshold;
    var freeReached = subtotal >= T.freeThreshold;
    this.shippingBar.classList.toggle('cart-drawer__shipping-bar--reduced', reducedReached && !freeReached);
    this.shippingBar.classList.toggle('cart-drawer__shipping-bar--complete', freeReached);

    // Shop money_format hat kein €-Symbol → Beträge im Versandbalken explizit mit € ausgeben
    var euro = function (c) { return (c / 100).toFixed(2).replace('.', ',') + ' €'; };
    if (!reducedReached) {
      this.shippingMsg.innerHTML = 'Nur noch <strong>' + euro(T.reducedThreshold - subtotal) +
        '</strong> bis zum reduzierten Versand.';
    } else if (!freeReached) {
      this.shippingMsg.innerHTML = 'Reduzierter Versand freigeschaltet ✓ – nur noch <strong>' +
        euro(T.freeThreshold - subtotal) + '</strong> bis zum <strong>kostenlosen Premium Versand!</strong>';
    } else {
      this.shippingMsg.innerHTML = 'Geschafft – <strong>kostenloser Premium Versand!</strong>';
    }
  };

  CartDrawer.prototype._renderCartDiscounts = function (cart) {
    if (!this.cartDiscountsEl) return;
    this.cartDiscountsEl.innerHTML = '';
    if (!cart.cart_level_discount_applications || cart.cart_level_discount_applications.length === 0) return;

    for (var i = 0; i < cart.cart_level_discount_applications.length; i++) {
      var d = cart.cart_level_discount_applications[i];
      if (!d.total_allocated_amount || d.total_allocated_amount <= 0) continue;
      var row = document.createElement('div');
      row.className = 'cart-drawer__cart-discount-row';
      var titleSpan = document.createElement('span');
      titleSpan.textContent = d.title;
      var amountSpan = document.createElement('span');
      amountSpan.textContent = '-' + formatMoney(d.total_allocated_amount);
      row.appendChild(titleSpan);
      row.appendChild(amountSpan);
      this.cartDiscountsEl.appendChild(row);
    }

    // Also check line-level discounts for total savings display
    var totalLineSavings = 0;
    if (cart.items) {
      for (var j = 0; j < cart.items.length; j++) {
        var allocs = cart.items[j].line_level_discount_allocations;
        if (allocs) {
          for (var k = 0; k < allocs.length; k++) {
            totalLineSavings += allocs[k].amount;
          }
        }
      }
    }
    if (totalLineSavings > 0 && this.cartDiscountsEl.children.length === 0) {
      var sRow = document.createElement('div');
      sRow.className = 'cart-drawer__cart-discount-row';
      var sTitle = document.createElement('span');
      sTitle.textContent = 'Rabatt';
      var sAmount = document.createElement('span');
      sAmount.textContent = '-' + formatMoney(totalLineSavings);
      sRow.appendChild(sTitle);
      sRow.appendChild(sAmount);
      this.cartDiscountsEl.appendChild(sRow);
    }
  };

  CartDrawer.prototype._renderAppliedDiscounts = function (cart) {
    if (!this.discountApplied) return;
    this.discountApplied.innerHTML = '';
    if (!cart.discount_codes || cart.discount_codes.length === 0) return;

    for (var i = 0; i < cart.discount_codes.length; i++) {
      var code = cart.discount_codes[i];
      if (!code.applicable) continue;
      var tag = document.createElement('span');
      tag.className = 'cart-drawer__discount-tag';
      tag.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg> ' +
        escapeHtml(code.code) +
        '<button type="button" data-remove-discount="' + escapeHtml(code.code) + '" aria-label="Rabattcode entfernen">' +
        '<svg width="10" height="10" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
        '</button>';
      this.discountApplied.appendChild(tag);
    }
  };

  /* -------------------------------------------------------
     Upsells (Shopify Product Recommendations API)
     ------------------------------------------------------- */

  CartDrawer.prototype._fetchUpsells = function (cart) {
    if (!this.upsellSection || !this.upsellList || !this.upsellTemplate) return;
    if (!CFG.recommendationsUrl || cart.items.length === 0) {
      this.upsellSection.style.display = 'none';
      return;
    }

    var self = this;
    var cartProductIds = cart.items.map(function (item) { return item.product_id; });

    // Use the most expensive cart item as the seed (that's the main purchase)
    var seedItem = cart.items[0];
    for (var i = 1; i < cart.items.length; i++) {
      if (cart.items[i].price > seedItem.price) {
        seedItem = cart.items[i];
      }
    }
    var seedId = seedItem.product_id;

    // Cache key: seed product + cart contents (re-fetch when either changes)
    var cacheKey = seedId + ':' + cartProductIds.slice().sort().join(',');
    if (cacheKey === this._lastUpsellProductId) return;
    this._lastUpsellProductId = cacheKey;

    // Single fetch: complementary products for the main item
    fetch(CFG.recommendationsUrl + '.json?product_id=' + seedId + '&limit=10&intent=complementary')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = (data.products || []).filter(function (p) {
          return cartProductIds.indexOf(p.id) === -1 && p.available;
        }).slice(0, 3);

        if (products.length < 3) {
          // Fill with related products as fallback
          return fetch(CFG.recommendationsUrl + '.json?product_id=' + seedId + '&limit=10&intent=related')
            .then(function (r2) { return r2.json(); })
            .then(function (data2) {
              var seenIds = {};
              for (var k = 0; k < products.length; k++) seenIds[products[k].id] = true;
              var related = (data2.products || []).filter(function (p) {
                return !seenIds[p.id] && cartProductIds.indexOf(p.id) === -1 && p.available;
              });
              return products.concat(related).slice(0, 3);
            });
        }
        return products;
      })
      .then(function (products) {
        self._renderUpsells(products);
      })
      .catch(function () {
        self.upsellSection.style.display = 'none';
      });
  };

  CartDrawer.prototype._renderUpsells = function (products) {
    if (!products || products.length === 0) {
      this.upsellSection.style.display = 'none';
      return;
    }

    this.upsellList.innerHTML = '';
    for (var i = 0; i < products.length; i++) {
      var card = this._renderUpsellCard(products[i]);
      if (card) this.upsellList.appendChild(card);
    }
    this.upsellSection.style.display = '';
  };

  CartDrawer.prototype._renderUpsellCard = function (product) {
    if (!this.upsellTemplate) return null;
    var tpl = this.upsellTemplate.content.cloneNode(true);
    var card = tpl.querySelector('[data-upsell-card]');
    if (!card) return null;

    // Use the first available variant
    var variant = product.variants[0];
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available) {
        variant = product.variants[i];
        break;
      }
    }

    card.setAttribute('data-upsell-variant-id', variant.id);

    // Image
    var img = card.querySelector('[data-upsell-img]');
    if (img && product.featured_image) {
      img.src = resizeImage(product.featured_image, '160x');
      img.alt = product.title;
    }

    // Title
    var title = card.querySelector('[data-upsell-title]');
    if (title) {
      title.textContent = product.title;
      title.href = product.url;
    }

    // Price
    var price = card.querySelector('[data-upsell-price]');
    if (price) {
      price.textContent = formatMoney(variant.price);
    }

    // Compare-at price
    var compare = card.querySelector('[data-upsell-compare]');
    if (compare) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compare.textContent = formatMoney(variant.compare_at_price);
      } else {
        compare.style.display = 'none';
      }
    }

    return card;
  };

  CartDrawer.prototype._fetchBestsellers = function () {
    if (!this.bestsellersList || !this.upsellTemplate) return;

    var self = this;

    // If we already have cached data, just re-render (resets button states)
    if (this._bestsellersData) {
      this._renderBestsellerCards(this._bestsellersData);
      return;
    }

    fetch('/collections/all/products.json?sort_by=best-selling&limit=8')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = (data.products || []).filter(function (p) {
          return p.variants && p.variants.some(function (v) { return v.available; });
        }).slice(0, 4);

        self._bestsellersData = products;
        self._renderBestsellerCards(products);
      })
      .catch(function () {
        if (self.bestsellersEl) self.bestsellersEl.style.display = 'none';
      });
  };

  CartDrawer.prototype._renderBestsellerCards = function (products) {
    this.bestsellersList.innerHTML = '';
    for (var i = 0; i < products.length; i++) {
      var card = this._renderBestsellerCard(products[i]);
      if (card) this.bestsellersList.appendChild(card);
    }
  };

  CartDrawer.prototype._renderBestsellerCard = function (product) {
    if (!this.upsellTemplate) return null;
    var tpl = this.upsellTemplate.content.cloneNode(true);
    var card = tpl.querySelector('[data-upsell-card]');
    if (!card) return null;

    // Find first available variant
    var variant = product.variants[0];
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available) {
        variant = product.variants[i];
        break;
      }
    }

    card.setAttribute('data-upsell-variant-id', variant.id);

    // Image — products.json uses product.images[] array
    var img = card.querySelector('[data-upsell-img]');
    if (img) {
      var imgSrc = '';
      if (product.images && product.images.length > 0) {
        imgSrc = product.images[0].src;
      }
      if (imgSrc) {
        img.src = resizeImage(imgSrc, '160x');
        img.alt = product.title;
      } else {
        img.style.display = 'none';
      }
    }

    // Title
    var title = card.querySelector('[data-upsell-title]');
    if (title) {
      title.textContent = product.title;
      title.href = '/products/' + product.handle;
    }

    // Benefits — fetched async via Section Rendering API
    var desc = card.querySelector('[data-upsell-desc]');
    if (desc && product.handle) {
      this._fetchProductBenefits(product.handle, desc);
    }

    // Price — products.json returns price as string "82.39", convert to cents
    var price = card.querySelector('[data-upsell-price]');
    if (price) {
      price.textContent = formatMoney(Math.round(parseFloat(variant.price) * 100));
    }

    // Compare-at price
    var compare = card.querySelector('[data-upsell-compare]');
    if (compare) {
      if (variant.compare_at_price && parseFloat(variant.compare_at_price) > parseFloat(variant.price)) {
        compare.textContent = formatMoney(Math.round(parseFloat(variant.compare_at_price) * 100));
      } else {
        compare.style.display = 'none';
      }
    }

    return card;
  };

  CartDrawer.prototype._fetchProductBenefits = function (handle, descEl) {
    fetch('/products/' + handle + '?section_id=cart-drawer-product-meta')
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var match = html.match(/data-product-benefits[^>]*>([\s\S]*?)<\/script>/);
        if (!match) return;
        var benefits;
        try { benefits = JSON.parse(match[1].trim()); } catch (e) { return; }
        if (!Array.isArray(benefits) || benefits.length === 0) return;

        var checkSvg = '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" stroke-width="3"><polyline points="20 6 9 17 4 12"></polyline></svg>';
        var lines = '';
        var max = Math.min(benefits.length, 3);
        for (var i = 0; i < max; i++) {
          lines += '<span class="cart-drawer__benefit-line">' + checkSvg + ' ' + escapeHtml(benefits[i]) + '</span>';
        }
        descEl.innerHTML = lines;
      })
      .catch(function () {});
  };

  CartDrawer.prototype._addUpsellToCart = function (variantId, button) {
    var self = this;
    button.disabled = true;
    button.textContent = '...';

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
      .then(function (r) { return r.json(); })
      .then(function () { return self._fetchCart(); })
      .then(function (cart) {
        self.cart = cart;
        self._lastUpsellProductId = null; // force re-fetch upsells
        self._renderCart(cart);
        self._dispatchCartUpdate(cart);
      })
      .catch(function () {
        button.disabled = false;
        button.textContent = '+';
      });
  };

  CartDrawer.prototype._showLoading = function (show) {
    if (this.loadingEl) {
      this.loadingEl.style.display = show ? '' : 'none';
    }
  };

  /* -------------------------------------------------------
     Quantity & Remove
     ------------------------------------------------------- */

  CartDrawer.prototype._changeQuantity = function (key, quantity) {
    var self = this;
    this._setItemLoading(key, true);

    fetch(CFG.cartChangeUrl + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) {
        if (!r.ok) {
          return r.json().then(function (err) {
            throw new Error(err.description || err.message || 'Fehler');
          });
        }
        return r.json();
      })
      .then(function (cart) {
        self.cart = cart;
        self._renderCart(cart);
        self._dispatchCartUpdate(cart);
      })
      .catch(function (err) {
        self._setItemLoading(key, false);
        // Show error briefly (e.g., stock limit)
        if (err && err.message) {
          self._showItemError(key, err.message);
        }
      });
  };

  CartDrawer.prototype._removeItem = function (key) {
    var self = this;
    var item = this.itemsList.querySelector('[data-cart-drawer-item-key="' + key + '"]');
    if (item) {
      item.classList.add('cart-drawer__item--removing');
    }
    setTimeout(function () {
      self._changeQuantity(key, 0);
    }, 300);
  };

  CartDrawer.prototype._setItemLoading = function (key, loading) {
    var item = this.itemsList.querySelector('[data-cart-drawer-item-key="' + key + '"]');
    if (!item) return;
    var btns = item.querySelectorAll('.cart-drawer__qty-btn, [data-item-remove]');
    for (var i = 0; i < btns.length; i++) {
      btns[i].disabled = loading;
    }
    item.style.opacity = loading ? '0.5' : '';
  };

  CartDrawer.prototype._showItemError = function (key, message) {
    var item = this.itemsList.querySelector('[data-cart-drawer-item-key="' + key + '"]');
    if (!item) return;
    var existing = item.querySelector('.cart-drawer__item-error');
    if (existing) existing.remove();
    var errEl = document.createElement('p');
    errEl.className = 'cart-drawer__item-error';
    errEl.textContent = message;
    item.querySelector('.cart-drawer__item-info').appendChild(errEl);
    setTimeout(function () { errEl.remove(); }, 4000);
  };

  /* -------------------------------------------------------
     Discount codes
     ------------------------------------------------------- */

  CartDrawer.prototype._applyDiscount = function (code) {
    if (!code || !code.trim()) return;
    var self = this;

    this.discountApplyBtn.disabled = true;
    this.discountError.style.display = 'none';

    fetch(CFG.cartUpdateUrl + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: code.trim() })
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        return self._fetchCart();
      })
      .then(function (cart) {
        self.cart = cart;
        self.discountApplyBtn.disabled = false;
        self.discountInput.value = '';

        var applied = false;
        if (cart.discount_codes) {
          for (var i = 0; i < cart.discount_codes.length; i++) {
            if (cart.discount_codes[i].code.toLowerCase() === code.trim().toLowerCase()) {
              applied = true;
              break;
            }
          }
        }

        if (applied) {
          self._renderCart(cart);
          self._dispatchCartUpdate(cart);
        } else {
          self.discountError.textContent = 'Ung\u00fcltiger Rabattcode';
          self.discountError.style.display = '';
        }
      })
      .catch(function () {
        self.discountApplyBtn.disabled = false;
        self.discountError.textContent = 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
        self.discountError.style.display = '';
      });
  };

  CartDrawer.prototype._removeDiscount = function () {
    var self = this;
    fetch(CFG.cartUpdateUrl + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: '' })
    })
      .then(function (r) { return r.json(); })
      .then(function () { return self._fetchCart(); })
      .then(function (cart) {
        self.cart = cart;
        self._renderCart(cart);
        self._dispatchCartUpdate(cart);
      });
  };

  /* -------------------------------------------------------
     Cart count event (sync with header badge)
     ------------------------------------------------------- */

  CartDrawer.prototype._dispatchCartUpdate = function (cart) {
    this._selfUpdate = true;
    var self = this;
    window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
    setTimeout(function () { self._selfUpdate = false; }, 100);
  };

  /* -------------------------------------------------------
     Event binding
     ------------------------------------------------------- */

  CartDrawer.prototype._bindEvents = function () {
    var self = this;

    // Close button
    this.drawer.querySelector('[data-cart-drawer-close]').addEventListener('click', function () {
      self.close();
    });

    // Backdrop click
    this.backdrop.addEventListener('click', function () {
      self.close();
    });

    // ESC key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.isOpen) {
        self.close();
      }
    });

    // Header cart toggle(s) — delegated for dynamic elements
    document.addEventListener('click', function (e) {
      var toggle = e.target.closest('[data-cart-drawer-toggle]');
      if (toggle) {
        e.preventDefault();
        e.stopPropagation();
        self.open();
      }
    });

    // Quantity buttons (delegated)
    var debouncedChange = debounce(function (key, qty) {
      self._changeQuantity(key, qty);
    }, 300);

    this.itemsList.addEventListener('click', function (e) {
      // Wunschfarbton-Guard: "Farbton wählen" entfernt die farblose Position und
      // öffnet das Produkt zur Neuauswahl (vermeidet eine doppelte Position).
      var fixColor = e.target.closest('[data-fix-color]');
      if (fixColor) {
        e.preventDefault();
        var fixKey = fixColor.getAttribute('data-line-key');
        var fixDest = fixColor.getAttribute('href');
        fetch(CFG.cartUrl + '/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: fixKey, quantity: 0 })
        }).then(function () { window.location.href = fixDest; })
          .catch(function () { window.location.href = fixDest; });
        return;
      }

      var minus = e.target.closest('[data-qty-minus]');
      var plus = e.target.closest('[data-qty-plus]');
      var remove = e.target.closest('[data-item-remove]');
      var itemEl = e.target.closest('[data-cart-drawer-item]');
      if (!itemEl) return;
      var key = itemEl.getAttribute('data-cart-drawer-item-key');
      var input = itemEl.querySelector('[data-qty-input]');

      if (minus && input) {
        var val = parseInt(input.value, 10);
        if (val > 1) {
          input.value = val - 1;
          debouncedChange(key, val - 1);
        }
      } else if (plus && input) {
        var val2 = parseInt(input.value, 10);
        input.value = val2 + 1;
        debouncedChange(key, val2 + 1);
      } else if (remove) {
        self._removeItem(key);
      }
    });

    // Quantity input direct change
    this.itemsList.addEventListener('change', function (e) {
      if (!e.target.matches('[data-qty-input]')) return;
      var itemEl = e.target.closest('[data-cart-drawer-item]');
      if (!itemEl) return;
      var key = itemEl.getAttribute('data-cart-drawer-item-key');
      var val = parseInt(e.target.value, 10);
      if (val >= 1) {
        debouncedChange(key, val);
      } else {
        e.target.value = 1;
        debouncedChange(key, 1);
      }
    });

    // Wunschfarbton-Guard: Checkout blockieren, solange ein Farbton fehlt
    if (this.checkoutBtn) {
      this.checkoutBtn.addEventListener('click', function (e) {
        if (self.checkoutBtn.dataset.colorBlocked === 'true') {
          e.preventDefault();
          e.stopPropagation();
        }
      });
    }

    // Discount apply button
    if (this.discountApplyBtn) {
      this.discountApplyBtn.addEventListener('click', function () {
        self._applyDiscount(self.discountInput.value);
      });
    }

    // Discount input enter key
    if (this.discountInput) {
      this.discountInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          self._applyDiscount(self.discountInput.value);
        }
      });
    }

    // Remove discount (delegated)
    if (this.discountApplied) {
      this.discountApplied.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-remove-discount]');
        if (btn) {
          self._removeDiscount();
        }
      });
    }

    // Listen for cartcount:update (from AddToCartFlyout or other sources)
    window.addEventListener('cartcount:update', function () {
      if (self._selfUpdate) return;

      // Skip auto-open on /cart page
      if (document.querySelector('[data-section-type="static-cart"]')) return;

      // Clear any pending timer
      clearTimeout(self._openTimer);

      // Open quickly since flyout is hidden
      self._openTimer = setTimeout(function () {
        self.open();
      }, 400);
    });

    // Upsell + bestseller add-to-cart buttons (delegated on drawer body)
    this.bodyEl.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-upsell-add]');
      if (!btn) return;
      var card = btn.closest('[data-upsell-card]');
      if (!card) return;
      var variantId = parseInt(card.getAttribute('data-upsell-variant-id'), 10);
      if (variantId) {
        self._addUpsellToCart(variantId, btn);
      }
    });

    // Prevent scroll pass-through on backdrop (mobile)
    this.backdrop.addEventListener('touchmove', function (e) {
      e.preventDefault();
    }, { passive: false });
  };

  /* =========================================================
     Init
     ========================================================= */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      window.cartDrawer = new CartDrawer();
    });
  } else {
    window.cartDrawer = new CartDrawer();
  }

})();
